/**
 * Icelandic kennitala, validated in the browser with the same rules the
 * server uses (`backend/auth.py::validate_kennitala` + `company_kennitala_error`).
 *
 * iter359: the paywall only checked "ten digits", so a typo was accepted,
 * stored, and only surfaced days later when Konto refused the customer
 * record with nothing on screen. Same answer on both sides, said once.
 */
const WEIGHTS = [3, 2, 7, 6, 5, 4, 3, 2];

export const onlyDigits = (s) => (s || '').replace(/\D/g, '');

/** "" when the kennitala is fine, otherwise the Icelandic reason. */
export const kennitalaError = (raw, lang = 'is') => {
  const d = onlyDigits(raw);
  const en = lang === 'en';
  if (!d) return en ? 'Kennitala is required.' : 'Kennitala vantar.';
  if (d.length !== 10) {
    return en ? 'Kennitala must be 10 digits.' : 'Kennitala verður að vera 10 tölustafir.';
  }
  const sum = WEIGHTS.reduce((acc, w, i) => acc + Number(d[i]) * w, 0);
  const remainder = sum % 11;
  const expected = remainder === 0 ? 0 : 11 - remainder;
  if (remainder === 1 || Number(d[8]) !== expected) {
    return en ? 'Invalid kennitala (checksum).' : 'Ógild kennitala (rangt vartala).';
  }
  if (!['0', '8', '9'].includes(d[9])) {
    return en ? 'Invalid kennitala (century digit).' : 'Ógild kennitala (rangt aldarmerki).';
  }
  const day = Number(d.slice(0, 2));
  const month = Number(d.slice(2, 4));
  const realDay = day > 40 ? day - 40 : day;
  if (realDay < 1 || realDay > 31 || month < 1 || month > 12) {
    return en ? 'Invalid kennitala (date part).' : 'Ógild kennitala (rangur dagsetningarhluti).';
  }
  return '';
};

export const isCompanyKennitala = (raw) => Number(onlyDigits(raw).slice(0, 2)) > 40;

/** A félag has a COMPANY kennitala: the day digits plus 40, so 41 and up. */
export const looksLikeFelag = (companyName) => {
  const n = (companyName || '').toLowerCase();
  return [' ehf', 'ehf.', ' hf.', ' slf', 'slf.', ' ses', 'ses.', ' svf', 'svf.']
    .some((marker) => n.includes(marker));
};

/** "" when the pair is fine, otherwise the Icelandic reason. */
export const companyKennitalaError = (raw, companyName, lang = 'is') => {
  const base = kennitalaError(raw, lang);
  if (base) return base;
  if (looksLikeFelag(companyName) && !isCompanyKennitala(raw)) {
    return lang === 'en'
      ? 'That is a personal kennitala. A company (ehf., hf., slf.) has a company kennitala starting at 41.'
      : 'Þetta er kennitala einstaklings. Félag (ehf., hf., slf.) hefur kennitölu fyrirtækis, hún byrjar á 41 eða hærri. Sláðu inn kennitölu félagsins.';
  }
  return '';
};
